import { useState, useCallback } from 'react';
import { mutate } from '../graphql/client';
import { GET_AVATAR_UPLOAD_URL } from '../graphql/operations';
import type { AvatarUploadUrl } from '../types/api';
import { processAvatarImage } from '../utils/imageProcessor';

interface UseAvatarUploadResult {
  uploadAvatar: (file: File) => Promise<string>;
  uploading: boolean;
  progress: number;
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for uploading avatar images using presigned S3 URLs
 *
 * Flow:
 * 1. Get presigned upload URL from backend
 * 2. Upload file directly to S3
 * 3. Return the public avatar URL to be saved in profile
 */
export function useAvatarUpload(): UseAvatarUploadResult {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  const uploadAvatar = useCallback(async (file: File): Promise<string> => {
    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      // Step 1: Process image client-side (resize, compress, convert)
      const processed = await processAvatarImage(file);
      setProgress(20);

      // Step 2: Get presigned URL from backend with processed content type
      const data = await mutate<{ getAvatarUploadUrl: AvatarUploadUrl }>(
        GET_AVATAR_UPLOAD_URL,
        { contentType: processed.type }
      );

      const { uploadUrl, avatarUrl } = data.getAvatarUploadUrl;
      setProgress(40);

      // Step 3: Upload processed image directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: processed.blob,
        headers: {
          'Content-Type': processed.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      setProgress(100);

      // Step 4: Return the public URL
      return avatarUrl;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to upload avatar');
      setError(error);
      throw error;
    } finally {
      setUploading(false);
    }
  }, []);

  return {
    uploadAvatar,
    uploading,
    progress,
    error,
    reset,
  };
}
